//react component
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { createYupSchema } from "../../utilities/yupSchemaCreator";
import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { OdooAccountFields } from "./Fields";
import {
  Alert,
  Box,
  Button,
  Card,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import styled from "styled-components";
import ErrorsList from "../../ui/ErrorsList";
import { useOdooInvoices } from "./provider";

const yupSchema = OdooAccountFields.reduce(createYupSchema, {});
const formSchema = Yup.object().shape(yupSchema);

const GridGroup = styled.div`
  max-width: 1210px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  row-gap: 0;
  padding: 20px;
  margin-bottom: 20px;
  grid-template-rows: 80px;
`;

const ButtonWrapper = styled.div`
  margin-bottom: 50px;
  button {
    float: right;
    margin-left: 20px;
  }
`;
const FormOdooAccount = ({ account, handleSuccess }) => {
  let navigate = useNavigate();
  const { id, name } = useParams();
  let theme = useTheme();
  const [error, setError] = useState(null);
  const { create, edit } = useOdooInvoices();
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(formSchema),
    defaultValues: account,
  });
  const getDefaultChecked = (fieldName, formName, account) => {
    if (fieldName === "isActive") return account?.isActive ?? true;

    return true;
  };

  useEffect(() => {
    setValue("locked", name === "main" ? true : false);
  }, [setValue, name]);
  const onSubmit = async (values) => {
    try {
      if (account) {
        await edit(values, id);
      } else {
        await create(values);
      }
      handleSuccess();
    } catch (error) {
      setError({ title: error?.errors?.message?.toString() });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Card
        sx={{
          p: theme.dimensions.padding,
          mt: theme.dimensions.mt,
          mb: theme.dimensions.mb,
        }}
      >
        <Typography variant="Poster" mb={3} component="h4" gutterBottom>
          Odoo Account Details
        </Typography>
        {error ? (
          <Alert severity="error" sx={{ m: 3 }}>
            <ErrorsList error={error} />
          </Alert>
        ) : (
          ""
        )}
        <GridGroup>
          {OdooAccountFields?.map((f, i) => {
            if (f.viewOnly) return null;
            const inputProps = { ...f.inputProps };

            if (i === 0) inputProps.autoFocus = true;
            inputProps.type = f.type;
            if (!account || name === "expense") {
              return (
                <Box key={i}>
                  {f.switch ? (
                    <Box>
                      <FormGroup>
                        <FormLabel
                          component="legend"
                          sx={{
                            mt: theme.dimensions.mt,
                          }}
                        >
                          {f.label}
                        </FormLabel>
                        <FormControlLabel
                          sx={{
                            mt: theme.dimensions.mt,
                          }}
                          control={
                            <Switch
                              color="success"
                              disabled={f.name === "locked"}
                              defaultChecked={getDefaultChecked(
                                f.name,
                                name,
                                account,
                              )}
                            />
                          }
                          {...register(f?.name)}
                          label="Active"
                        />
                      </FormGroup>
                    </Box>
                  ) : f.select ? (
                    <FormControl
                      sx={{
                        width: theme.dimensions.width,
                        mt: theme.dimensions.mt,
                        mb: theme.dimensions.mb,
                      }}
                      required
                      error={!!errors[f.name]}
                    >
                      <InputLabel id="demo-simple-select-label">
                        {f.label}
                      </InputLabel>
                      <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        defaultValue={account?.typeId}
                        label={f.label}
                        inputProps={{
                          ...register(f?.name),
                          required: true,
                        }}
                        size="small"
                      >
                        {[
                          { typeId: 1, label: "Debit" },
                          { typeId: 2, label: "Credit" },
                        ]?.map((s, index) => (
                          <MenuItem key={index} value={s.typeId}>
                            {s.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <>
                      <TextField
                        label={f.label}
                        name={f.name}
                        error={!!errors[f.name]}
                        helperText={errors[f.name]?.message}
                        select={f.select}
                        className={f.class}
                        fullWidth
                        required
                        disabled={f.disabled}
                        sx={{
                          width: theme.dimensions.width,
                          mt: theme.dimensions.mt,
                          mb: theme.dimensions.mb,
                        }}
                        inputProps={{
                          ...register(f?.name),
                          required: f.required,
                        }}
                        size="small"
                      ></TextField>
                    </>
                  )}
                </Box>
              );
            } else if (account && name === "main" && f.name === "number") {
              return (
                <>
                  <TextField
                    label={f.label}
                    name={f.name}
                    error={!!errors[f.name]}
                    helperText={errors[f.name]?.message}
                    select={f.select}
                    className={f.class}
                    fullWidth
                    required
                    disabled={f.disabled}
                    sx={{
                      width: theme.dimensions.width,
                      mt: theme.dimensions.mt,
                      mb: theme.dimensions.mb,
                    }}
                    inputProps={{
                      ...register(f?.name),
                      required: f.required,
                    }}
                    size="small"
                  ></TextField>
                </>
              );
            }
          })}
        </GridGroup>
        <ButtonWrapper>
          <Button
            variant="contained"
            type="submit"
            sx={{
              backgroundColor: theme.color.blackBackground,
              ml: theme.dimensions.ml,
              "&:hover": { background: `${theme.color.black}` },
            }}
          >
            {account ? <>Update</> : <>Add Account</>}
          </Button>
          <Button
            variant="contained"
            onClick={() => navigate(-1)}
            sx={{
              backgroundColor: theme.color.grayBackground,
              color: theme.color.dark,
              "&:hover": { background: `${theme.color.gray50}` },
            }}
          >
            Discard
          </Button>
        </ButtonWrapper>
      </Card>
    </form>
  );
};

export default React.memo(FormOdooAccount);
